import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config';
import { withImpersonationHeader } from '../utils/impersonation';

interface BudgetEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentBudget: number;
  onBudgetUpdate: (newBudget: number) => void;
}

const BudgetEditModal: React.FC<BudgetEditModalProps> = ({
  isOpen,
  onClose,
  currentBudget,
  onBudgetUpdate
}) => {
  const { session } = useAuth();
  const [budgetAmount, setBudgetAmount] = useState(currentBudget);
  const [updateDefault, setUpdateDefault] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setBudgetAmount(currentBudget);
  }, [currentBudget]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const token = session?.access_token;
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(config.getApiUrl('/auth/budget'), {
        method: 'PUT',
        headers: withImpersonationHeader({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }),
        body: JSON.stringify({
          current_budget: budgetAmount,
          update_default: updateDefault,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update budget: ${response.status}`);
      }

      const result = await response.json();
      console.log('Budget updated successfully:', result);

      // Update the parent component
      onBudgetUpdate(budgetAmount);
      onClose();
    } catch (err: any) {
      console.error('Error updating budget:', err);
      setError(err.message || 'Failed to update budget');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setError(null);
      setUpdateDefault(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div
        className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-zinc-800">
          <h3 className="text-lg font-semibold text-zinc-200">Edit Monthly Budget</h3>
          <button
            className="text-zinc-400 hover:text-zinc-200 text-2xl transition-colors duration-200"
            onClick={handleClose}
            disabled={isLoading}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-sm">
              {error}
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="budget" className="block text-sm font-medium text-zinc-300 mb-2">
              Monthly Budget ($)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 text-sm">$</span>
              <input
                type="number"
                id="budget"
                min="0"
                step="0.01"
                value={budgetAmount}
                onChange={(e) => setBudgetAmount(parseFloat(e.target.value) || 0)}
                className="w-full pl-8 pr-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-200 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200"
                placeholder="100.00"
                disabled={isLoading}
                required
              />
            </div>
            <p className="mt-1 text-xs text-zinc-500">
              Current budget: ${currentBudget.toFixed(2)}
            </p>
          </div>

          <div className="mb-6">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={updateDefault}
                onChange={(e) => setUpdateDefault(e.target.checked)}
                className="mt-1 w-4 h-4 text-blue-600 bg-zinc-800 border-zinc-600 rounded focus:ring-blue-500 focus:ring-2"
                disabled={isLoading}
              />
              <div>
                <span className="text-sm font-medium text-zinc-300">
                  Set as default monthly budget
                </span>
                <p className="text-xs text-zinc-500 mt-1">
                  This will update your default budget and reset to this amount every month.
                </p>
              </div>
            </label>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg text-zinc-200 transition-all duration-200"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? 'Updating...' : 'Update Budget'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BudgetEditModal;
